import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import LoginPage from "@/pages/LoginPage";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import LembagaPage from "@/pages/Lembaga";
import SiswaPage from "@/pages/Siswa";
import TagihanPage from "@/pages/Tagihan";
import PembayaranPage from "@/pages/Pembayaran";
import PengeluaranPage from "@/pages/Pengeluaran";
import CashflowPage from "@/pages/Cashflow";
import LaporanKeuangan from "@/pages/LaporanKeuangan";
import EmployeesPage from "@/pages/Employees";
import SlipGajiPage from "@/pages/SlipGaji";
import CutiPage from "@/pages/Cuti";
import RaporPage from "@/pages/Rapor";
import RaporBiahPage from "@/pages/RaporBiah";
import RekrutmenPage from "@/pages/Rekrutmen";
import UsersPage from "@/pages/Users";
import SettingsPage from "@/pages/Settings";
import PortalKaryawan from "@/pages/PortalKaryawan";
import Kalender from "@/pages/Kalender";
import NomenklaturPage from "@/pages/Nomenklatur";
import AgendaManagerKeuangan from "@/pages/AgendaManagerKeuangan";
import RekapTagihan from "@/pages/RekapTagihan";
import KegiatanLPJ from "@/pages/KegiatanLPJ";
import PosKeuangan from "@/pages/PosKeuangan";
import WaliLoginPage from "@/pages/WaliLoginPage";
import PortalWali from "@/pages/PortalWali";
import RekeningYayasan from "@/pages/RekeningYayasan";
import ApprovalPembayaran from "@/pages/ApprovalPembayaran";
import { useIdleLogout } from "@/lib/useIdleLogout";

function ProtectedRoute({ children }) {
  const { user, logout } = useAuth();
  const { Modal: IdleModal } = useIdleLogout(() => logout?.(), !!user);
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFFFFF]">
        <div className="dot-pulse flex gap-1 text-[#0B8D95]">
          <span className="h-2 w-2 rounded-full bg-current" />
          <span className="h-2 w-2 rounded-full bg-current" />
          <span className="h-2 w-2 rounded-full bg-current" />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}<IdleModal /></>;
}

function RoleGuard({ allow, children, foundationOnly = false }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "superadmin") return children;
  if (!allow.includes(user.role)) return <Navigate to="/" replace />;
  if (foundationOnly && user.lembaga_id) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/portal-wali/login" element={<WaliLoginPage />} />
            <Route path="/portal-wali" element={<PortalWali />} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="lembaga" element={<RoleGuard allow={["admin","finance"]}><LembagaPage /></RoleGuard>} />
              <Route path="siswa" element={<RoleGuard allow={["admin","finance"]}><SiswaPage /></RoleGuard>} />
              <Route path="tagihan" element={<RoleGuard allow={["admin","finance"]}><TagihanPage /></RoleGuard>} />
              <Route path="rekap-tagihan" element={<RoleGuard allow={["admin","finance"]}><RekapTagihan /></RoleGuard>} />
              <Route path="kegiatan" element={<RoleGuard allow={["admin","hr","finance"]}><KegiatanLPJ /></RoleGuard>} />
              <Route path="pos-keuangan" element={<RoleGuard allow={["admin","finance"]}><PosKeuangan /></RoleGuard>} />
              <Route path="pembayaran" element={<RoleGuard allow={["admin","finance"]}><PembayaranPage /></RoleGuard>} />
              <Route path="pengeluaran" element={<RoleGuard allow={["admin","finance"]}><PengeluaranPage /></RoleGuard>} />
              <Route path="cashflow" element={<RoleGuard allow={["admin","finance"]}><CashflowPage /></RoleGuard>} />
              <Route path="laporan-keuangan" element={<RoleGuard allow={["admin","finance"]}><LaporanKeuangan /></RoleGuard>} />
              <Route path="employees" element={<RoleGuard allow={["admin","hr"]}><EmployeesPage /></RoleGuard>} />
              <Route path="slip-gaji" element={<RoleGuard allow={["admin","hr","karyawan"]}><SlipGajiPage /></RoleGuard>} />
              <Route path="cuti" element={<RoleGuard allow={["admin","hr","karyawan"]}><CutiPage /></RoleGuard>} />
              <Route path="rapor" element={<RoleGuard allow={["admin","hr","karyawan"]}><RaporPage /></RoleGuard>} />
              <Route path="rapor-biah" element={<RoleGuard allow={["admin","hr","karyawan"]}><RaporBiahPage /></RoleGuard>} />
              <Route path="rekrutmen" element={<RoleGuard allow={["admin","hr"]}><RekrutmenPage /></RoleGuard>} />
              <Route path="users" element={<RoleGuard allow={["admin"]}><UsersPage /></RoleGuard>} />
              <Route path="settings" element={<RoleGuard allow={["admin"]} foundationOnly><SettingsPage /></RoleGuard>} />
              <Route path="portal" element={<PortalKaryawan />} />
              <Route path="kalender" element={<Kalender />} />
              <Route path="nomenklatur" element={<RoleGuard allow={["admin","finance"]}><NomenklaturPage /></RoleGuard>} />
              <Route path="rekening" element={<RoleGuard allow={[]}><RekeningYayasan /></RoleGuard>} />
              <Route path="approval-pembayaran" element={<RoleGuard allow={["admin","finance"]}><ApprovalPembayaran /></RoleGuard>} />
              <Route path="agenda-manager-keuangan" element={<RoleGuard allow={[]}><AgendaManagerKeuangan /></RoleGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
